import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { extractGraphQLError } from "@/features/review/api/errors";
import { buildMesuresByIdsQuery, buildMesuresByIdsVariables } from "../api/documents";
import type { MesureMinimalFragment, MesuresByIdsQueryResult } from "../types/graphql";

/**
 * Resolves the `mesureIds` already parsed by `parseMesureIdsParam` through
 * the single aliased multi-`mesure(id)` document built by
 * `buildMesuresByIdsQuery` — exactly one Apollo `useQuery` call regardless
 * of how many ids were requested (§3.4).
 *
 * Error classification is not reinvented here: `extractGraphQLError`/
 * `ReviewErrorViewModel` (`features/review/api/errors.ts`) are reused
 * as-is, exactly like `N1CockpitPage` already reuses
 * `features/review/hooks/useSessionExpiryRedirect` — this is an
 * established cross-feature reuse precedent in this repository, not a new
 * one introduced here.
 */
export function useN3Mesures(mesureIds: readonly string[]) {
  // Stable primitive key so `useMemo`/Apollo don't rebuild the document or
  // refire the query on every render just because the *array instance*
  // (not its content) changed — `mesureIds` itself comes fresh from
  // `parseMesureIdsParam` on every render of the caller.
  const idsKey = mesureIds.join("\u0000");

  const document = useMemo(
    () => buildMesuresByIdsQuery(mesureIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `idsKey` is the intentional, content-based dependency; `mesureIds` itself is deliberately excluded (see comment above).
    [idsKey],
  );
  const variables = useMemo(
    () => buildMesuresByIdsVariables(mesureIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see `document` above.
    [idsKey],
  );

  const { data, previousData, loading, error, refetch } = useQuery<
    MesuresByIdsQueryResult,
    Record<string, string>
  >(document, {
    variables,
    skip: mesureIds.length === 0,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const effective = data ?? previousData;
  const isInitialLoading =
    mesureIds.length > 0 && loading && data === undefined && previousData === undefined;

  // Alias index == position in `mesureIds` (`buildMesuresByIdsQuery`'s own
  // contract) — a direct, deterministic lookup back to the requested id,
  // never a re-sort/re-match by value.
  const mesures = useMemo<readonly MesureMinimalFragment[]>(() => {
    if (!effective) return [];
    const resolved: MesureMinimalFragment[] = [];
    mesureIds.forEach((_, index) => {
      const value = effective[`m${index}`];
      if (value !== null && value !== undefined) resolved.push(value);
    });
    return resolved;
  }, [effective, mesureIds]);

  const notFoundCount = effective ? mesureIds.length - mesures.length : 0;

  return {
    mesures,
    notFoundCount,
    isInitialLoading,
    error: extractGraphQLError(error),
    refetch,
  };
}
